import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { SbiToast, SbiToastType } from '../sbi-toast.model';

const ICON_CLASS: Record<SbiToastType, string> = {
  success: 'pi pi-check-circle',
  error: 'pi pi-times-circle',
  warning: 'pi pi-exclamation-triangle',
  info: 'pi pi-info-circle',
};

const EXIT_ANIMATION_MS = 300;

@Component({
  selector: 'sbi-toast-item',
  standalone: true,
  templateUrl: './sbi-toast-item.component.html',
  styleUrls: ['./sbi-toast-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SbiToastItemComponent implements OnInit, OnDestroy {
  readonly toast = input.required<SbiToast>();
  readonly dismissed = output<string>();

  readonly isActive = signal(false);
  readonly isPaused = signal(false);

  private _startTime = 0;
  private _elapsed = 0;
  private _timer: ReturnType<typeof setTimeout> | null = null;
  private _exitTimer: ReturnType<typeof setTimeout> | null = null;

  get iconClass(): string {
    return ICON_CLASS[this.toast().type];
  }

  get durationStyle(): string {
    return `${this.toast().duration}ms`;
  }

  get isUrgent(): boolean {
    const type = this.toast().type;
    return type === 'error' || type === 'warning';
  }

  ngOnInit(): void {
    // Small delay ensures the element is in the DOM before the CSS transition fires
    setTimeout(() => {
      this.isActive.set(true);
      this._startTimer();
    }, 10);
  }

  ngOnDestroy(): void {
    this._clearTimer();
    this._clearExitTimer();
  }

  onMouseEnter(): void {
    if (this._timer === null) return;
    this._clearTimer();
    this._elapsed += Date.now() - this._startTime;
    this.isPaused.set(true);
  }

  onMouseLeave(): void {
    if (!this.isPaused()) return;
    this.isPaused.set(false);
    this._startTimer();
  }

  onClose(): void {
    this._dismiss();
  }

  private _startTimer(): void {
    const remaining = this.toast().duration - this._elapsed;
    this._startTime = Date.now();
    this._timer = setTimeout(() => {
      this._timer = null;
      this._dismiss();
    }, remaining);
  }

  private _dismiss(): void {
    this._clearTimer();
    this.isActive.set(false);
    this._exitTimer = setTimeout(() => {
      this._exitTimer = null;
      this.dismissed.emit(this.toast().id);
    }, EXIT_ANIMATION_MS);
  }

  private _clearTimer(): void {
    if (this._timer !== null) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  private _clearExitTimer(): void {
    if (this._exitTimer !== null) {
      clearTimeout(this._exitTimer);
      this._exitTimer = null;
    }
  }
}
